import { computed, type MaybeRefOrGetter, type Ref, ref, toValue, watch } from 'vue';
import type { Row, Table } from '@tanstack/vue-table';
import type { ViewerState } from '@/modules/viewer/types/state.type.ts';
import { useViewerStore } from '@/modules/viewer/stores/viewer.store.ts';
import { isEqual, omit } from 'lodash-es';
import type { ToUniqString } from '@/modules/common/types/array.type.ts';

const acceptedSameIdUpdates = new Map<string, number>();
const lastSelectedIds = new Map<string, string>();

const ALWAYS_IGNORED_COMPARE_KEYS = ['original'] as const;
const OUTDATED_IGNORED_COMPARE_KEYS = ['original', 'indicator', 'isNew'] as const;

function acceptedKey(scope: string, id: string | number) {
  return `${scope}:${id}`;
}

export function acceptNextSameIdUpdate(scope: string, id: string | number) {
  const key = acceptedKey(scope, id);
  acceptedSameIdUpdates.set(key, (acceptedSameIdUpdates.get(key) ?? 0) + 1);
}

export function clearAcceptedSameIdUpdate(scope: string, id: string | number) {
  const key = acceptedKey(scope, id);
  const next = (acceptedSameIdUpdates.get(key) ?? 0) - 1;

  if (next <= 0) {
    acceptedSameIdUpdates.delete(key);
  } else {
    acceptedSameIdUpdates.set(key, next);
  }
}

export function clearAllAcceptedSameIdUpdates() {
  acceptedSameIdUpdates.clear();
}

export function getLastSelectedId(scope: string) {
  return lastSelectedIds.get(scope) ?? null;
}

function isAcceptedSameIdUpdate(scope: string, id: string) {
  return (acceptedSameIdUpdates.get(acceptedKey(scope, id)) ?? 0) > 0;
}

function toComparableRow<TData>(data: TData, ignoreReadMarkers: boolean) {
  if (data && typeof data === 'object') {
    return omit(data, ignoreReadMarkers ? OUTDATED_IGNORED_COMPARE_KEYS : ALWAYS_IGNORED_COMPARE_KEYS);
  }

  return data;
}

interface UseViewerSelectionParams<TData> {
  persistKey: string;
  state: Ref<ViewerState>;
  table: Table<TData>;
  items: Ref<TData[]>;
  isFetching: Ref<boolean>;
  isFetchedAfterMount: MaybeRefOrGetter<boolean>;
  getId: ToUniqString<TData>;
  deferSameIdUpdates?: boolean;
  scrollToIndex: (index: number) => void;
  onSelect: (data: TData | null) => void;
  onOutdated?: (data: TData | null) => void;
}

export function useViewerSelection<TData>({
  persistKey,
  state,
  table,
  items,
  isFetching,
  isFetchedAfterMount,
  getId,
  deferSameIdUpdates = false,
  scrollToIndex,
  onSelect,
  onOutdated,
}: UseViewerSelectionParams<TData>) {
  const viewerStore = useViewerStore();

  const selectedId = computed(() => Object.keys(state.value.rowSelection).at(0) ?? null);
  const outdatedData = ref<TData | null>(null);

  let prevId: string | null = null;
  let nextId: string | null = null;
  let prevSelectedId: string | null = null;
  let lastSelectedData: TData | null = null;
  let shouldScroll = true;
  let hasCompletedMountFetch = false;

  function completeMountFetchIfNeeded() {
    if (toValue(isFetchedAfterMount)) {
      hasCompletedMountFetch = true;
    }
  }

  function setOutdated(data: TData | null) {
    if (data === outdatedData.value) {
      return;
    }

    if (
      data &&
      outdatedData.value &&
      getId(data) === getId(outdatedData.value) &&
      isEqual(toComparableRow(data, true), toComparableRow(outdatedData.value, true))
    ) {
      outdatedData.value = data;
      return;
    }

    outdatedData.value = data;
    onOutdated?.(data);
  }

  function emitSelect(data: TData | null) {
    if (data === lastSelectedData) {
      return;
    }

    if (data && lastSelectedData && getId(data) === getId(lastSelectedData)) {
      if (!isEqual(toComparableRow(data, deferSameIdUpdates), toComparableRow(lastSelectedData, deferSameIdUpdates))) {
        if (deferSameIdUpdates) {
          if (!hasCompletedMountFetch || isAcceptedSameIdUpdate(persistKey, getId(data))) {
            lastSelectedData = data;
            setOutdated(null);
            onSelect(data);
            return;
          }

          setOutdated(data);
          return;
        }

        lastSelectedData = data;
        onSelect(data);
        return;
      }

      lastSelectedData = data;

      if (outdatedData.value !== null) {
        setOutdated(null);
      }

      return;
    }

    lastSelectedData = data;
    setOutdated(null);
    onSelect(data);
  }

  function clearSelect() {
    if (lastSelectedData === null && outdatedData.value === null) {
      return;
    }

    lastSelectedData = null;
    setOutdated(null);
    onSelect(null);
  }

  function getFallbackRow(rowsById: Record<string, Row<TData>>, rows: Row<TData>[]) {
    return (nextId && rowsById[nextId]) || (prevId && rowsById[prevId]) || rows[0];
  }

  function selectRow(row: Row<TData>) {
    shouldScroll = true;
    row.toggleSelected(true);
  }

  function restoreSelection(rowsById: Record<string, Row<TData>>, rows: Row<TData>[]) {
    const fallback = getFallbackRow(rowsById, rows);

    if (fallback) {
      selectRow(fallback);
    }
  }

  function applyOutdated() {
    const data = outdatedData.value;

    if (!data) {
      return;
    }

    const current = (selectedId.value && table.getRowModel().rowsById[selectedId.value]?.original) || data;

    lastSelectedData = current;
    setOutdated(null);
    onSelect(current);
  }

  watch(
    [isFetching, selectedId, items, () => toValue(isFetchedAfterMount)],
    () => {
      const fetching = isFetching.value;
      const currentSelectedId = selectedId.value;
      const { rowsById, rows } = table.getRowModel();
      const row = currentSelectedId && rowsById[currentSelectedId];

      if (currentSelectedId) {
        lastSelectedIds.set(persistKey, currentSelectedId);
      } else if (!fetching) {
        lastSelectedIds.delete(persistKey);
      }

      if (fetching) {
        if (row) {
          emitSelect(row.original);
        }
        return;
      }

      if (!items.value.length) {
        table.setRowSelection({});
        viewerStore.resetFirstPageLimit(persistKey);
        lastSelectedIds.delete(persistKey);
        clearSelect();
        prevSelectedId = null;
        prevId = null;
        nextId = null;
        shouldScroll = true;
        completeMountFetchIfNeeded();
        return;
      }

      if (row) {
        if (currentSelectedId !== prevSelectedId) {
          prevId = rows[row.index - 1]?.id ?? null;
          nextId = rows[row.index + 1]?.id ?? null;
          viewerStore.setFirstPageLimit(persistKey, row.index);
          prevSelectedId = currentSelectedId;

          if (shouldScroll) {
            setTimeout(() => {
              scrollToIndex(row.index);
              shouldScroll = false;
            });
          }
        }

        emitSelect(row.original);
        completeMountFetchIfNeeded();
        return;
      }

      restoreSelection(rowsById, rows);
      completeMountFetchIfNeeded();
    },
    { immediate: true }
  );

  function setSelectedId(id: string | null) {
    if (id === null) {
      table.setRowSelection({});
      return;
    }

    const row = table.getRowModel().rowsById[id];

    if (row) {
      selectRow(row);
    }
  }

  return {
    setSelectedId,
    applyOutdated,
  };
}
